import { createAsyncBundleLoader } from '../runtime/bundle_loader';
import { getXABundle } from '../runtime/pseudo/xa';
import { getXBBundle } from '../runtime/pseudo/xb';
import type { AppLocale, Bundle } from './types';
import { en_base } from './strings/en/base';

// The synchronous, eager-loading `getBundle()` lives in `./loader_sync.ts`.
// It is intentionally NOT re-exported here — same rationale as
// `@jumpstone/i18n`'s `costpro/loader.ts`: importing it would pull every
// locale string file into this module's static dependency graph, dragging
// si/ta/id/fr into the app's entry chunk and defeating the dynamic
// `import()` code-splitting below.
//
// Built on the shared `createAsyncBundleLoader` factory
// (`../runtime/bundle_loader.ts`) — same pattern as `ledger` and `costpro`.

/**
 * Returns the en-001 fallback bundle synchronously.
 * Always in the main JS chunk — zero network request, safe to call before
 * any async locale loading has happened.
 */
export function getFallbackBundle(): Bundle {
  return en_base;
}

/**
 * Loads the translation bundle for the given locale asynchronously.
 *
 * Locale resolution:
 *   en-001 → en_base (source of truth)
 *   si-LK  → en_base merged with si_base
 *   ta-LK  → en_base merged with ta_base
 *   id-ID  → en_base merged with id_base (not yet exposed by the runtime
 *            locale switcher — see types.ts)
 *   fr-CA  → en_base merged with fr_base (advisory locale; see types.ts;
 *            not yet exposed by the runtime locale switcher)
 *   en-XA  → pseudo-accented en_base (dev-only: exposes hardcoded strings)
 *   en-XB  → character-reversed en_base (dev-only: exposes RTL layout bugs)
 */
export const loadBundle = createAsyncBundleLoader<AppLocale>({
  getFallbackBundle,
  catalogName: 'wagepro',
  loaders: {
    'en-001': () => Promise.resolve(en_base),

    'si-LK': async () => {
      const { si_base } = await import('./strings/si/base');
      return { ...en_base, ...si_base };
    },

    'ta-LK': async () => {
      const { ta_base } = await import('./strings/ta/base');
      return { ...en_base, ...ta_base };
    },

    'id-ID': async () => {
      const { id_base } = await import('./strings/id/base');
      return { ...en_base, ...id_base };
    },

    'fr-CA': async () => {
      const { fr_base } = await import('./strings/fr/base');
      return { ...en_base, ...fr_base };
    },

    'en-XA': () => Promise.resolve(getXABundle(en_base)),
    'en-XB': () => Promise.resolve(getXBBundle(en_base)),
  },
});
